import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useThemeContext } from './ThemeProvider';
import { useI18n } from '../i18n/useI18n';
import KaraokrLogo from './KaraokrLogo';

interface FestivalHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  autocompleteCities: string[];
  onCitySelect: (city: string) => void;
  onClearSearch: () => void;
}

export default function FestivalHeader({
  searchQuery,
  onSearchChange,
  autocompleteCities,
  onCitySelect,
  onClearSearch,
}: FestivalHeaderProps) {
  const { resolvedTheme, toggleTheme } = useThemeContext();
  const { t } = useI18n();
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setAutocompleteOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCitySelect = (city: string) => {
    onCitySelect(city);
    setAutocompleteOpen(false);
  };

  return (
    <header className="festival-header">
      <div className="festival-header__inner">
        <Link to="/" className="festival-header__logo">
          <KaraokrLogo />
        </Link>

        <div className="festival-header__search" ref={searchRef}>
          <div className="festival-header__search-field">
            <svg className="festival-header__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="festival-header__search-input"
              placeholder={t('festival.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                onSearchChange(e.target.value);
                setAutocompleteOpen(true);
              }}
              onFocus={() => setAutocompleteOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setAutocompleteOpen(false);
              }}
            />
            {searchQuery && (
              <button
                type="button"
                className="festival-header__search-clear"
                onClick={onClearSearch}
                aria-label={t('common.clearSearch')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          {autocompleteOpen && autocompleteCities.length > 0 && (
            <ul className="festival-header__autocomplete">
              {autocompleteCities.map((city) => (
                <li key={city}>
                  <button
                    type="button"
                    className="festival-header__autocomplete-item"
                    onClick={() => handleCitySelect(city)}
                  >
                    <span className="festival-header__autocomplete-city">{city}</span>
                    <span className="festival-header__autocomplete-hint">{t('common.search')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="festival-header__actions">
          <button
            type="button"
            className="festival-header__icon-btn"
            onClick={toggleTheme}
            aria-label={resolvedTheme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
            title={resolvedTheme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
          >
            {resolvedTheme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <Link to="/join-room" className="festival-header__cta festival-header__cta--secondary">{t('common.joinRoom')}</Link>
          <Link to="/festival" className="festival-header__cta festival-header__cta--secondary">{t('common.events')}</Link>
          <Link to="/create-room" className="festival-header__cta">{t('common.createRoom')}</Link>
        </div>
      </div>
    </header>
  );
}
